import { Auth } from 'aws-amplify'

// Define enum for error types to ensure type safety
export enum CognitoErrorType {
  NO_CURRENT_USER = 'No current user',
  REFRESH_TOKEN_EXPIRED = 'Refresh Token has expired',
  INCORRECT_USERNAME_OR_PASSWORD = 'NotAuthorizedException',
}

// Define interfaces for token structure
interface JwtTokens {
  idToken: string
  accessToken: string
  refreshToken: string
}

interface DecodedTokens {
  idToken: Record<string, unknown>
  accessToken: Record<string, unknown>
}

interface Tokens {
  jwt: JwtTokens
  decoded: DecodedTokens
}

// Interface for sign-in credentials
interface SignInCredentials {
  username: string
  password: string
}

// Interface for stored route information
interface StoredRoute {
  path?: string
  [key: string]: unknown
}

/**
 * Authentication service for managing user authentication with AWS Cognito
 */
class AuthenticationServiceClass {
  /**
   * Retrieves the user's tokens from the current session
   * @returns The user's tokens or null if no user is logged in
   */
  public async getTokens(): Promise<Tokens | null> {
    try {
      const currentSession = await Auth.currentSession()

      const idToken = currentSession.getIdToken()
      const accessToken = currentSession.getAccessToken()
      const refreshToken = currentSession.getRefreshToken()

      return {
        jwt: {
          idToken: idToken.getJwtToken(),
          accessToken: accessToken.getJwtToken(),
          refreshToken: refreshToken.getToken(),
        },
        decoded: {
          idToken: idToken.decodePayload(),
          accessToken: accessToken.decodePayload(),
        },
      }
    } catch (error) {
      // Handle the specific case of no current user
      if (error === CognitoErrorType.NO_CURRENT_USER) {
        return null
      }

      // Log other errors but don't throw them
      console.error('Error getting tokens:', error)
      return null
    }
  }

  /**
   * Signs a user in with username and password
   * @param credentials User credentials containing username and password
   * @returns The authenticated user
   */
  public async signIn(credentials: SignInCredentials): Promise<unknown> {
    try {
      const { username, password } = credentials
      return await Auth.signIn(username, password)
    } catch (error) {
      // Preserve the original error type but wrap it for better typing
      throw new Error(error instanceof Error ? error.message : String(error))
    }
  }

  /**
   * Signs in via identity provider (social login)
   * @param provider The identity provider to use
   * @param storedRoute Optional route to redirect to after login
   */
  public async signInViaIdentityProvider(
    provider: string,
    storedRoute?: StoredRoute,
  ): Promise<void> {
    try {
      await Auth.federatedSignIn({
        provider,
        customState: storedRoute ? JSON.stringify(storedRoute) : undefined,
      })
    } catch (error) {
      console.error('Error when signing in with identity provider:', error)
      // We don't throw here to match the original behavior
    }
  }

  /**
   * Signs the current user out
   * @returns A promise that resolves when sign-out is complete
   */
  public async signOut(): Promise<unknown> {
    return Auth.signOut()
  }
}

// Export a singleton instance
export const AuthenticationService = new AuthenticationServiceClass()
